import {BackendResponse} from '@common/http/backend-response/backend-response';
import {useMutation} from '@tanstack/react-query';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import {apiClient, queryClient} from '@common/http/query-client';
import {showHttpErrorToast} from '@common/http/show-http-error-toast';
import {Likeable} from '@app/web-player/library/likeable';
import {userLibrary} from '@app/web-player/library/state/likes-store';
import { logEvent } from '@common/core/event-logger';

interface Response extends BackendResponse {}

interface Payload {
  likeables: Likeable[];
}

export function useAddItemsToLibrary(displayToast: boolean = true, displayToastError: boolean = true) {
  return useMutation({
    mutationFn: (payload: Payload) => addToLibrary(payload),
    onSuccess: (response, payload) => {
      if(displayToast) toast(getMessage(payload.likeables[0]));
      userLibrary().add(payload.likeables);

      payload.likeables.map(item => {
        logEvent({
          event_type: "addToLibrary",
          entity_id: item.id,
          entity_type: item.model_type,
          metadata: {[item.model_type]: item}
        });
      });
      
      // tracks/albums/artists
      queryClient.invalidateQueries({
        queryKey: [`${payload.likeables[0].model_type}s`, 'library'],
      });
    },
    onError: r => {
      if(displayToastError) {
        showHttpErrorToast(r)
      }
    },
  });
}

function addToLibrary(payload: Payload): Promise<Response> {
  const likeables = payload.likeables
    .filter(likeable => {
      return !userLibrary().has(likeable);
    })
    .map(likeable => {
      return {
        likeable_id: likeable.id,
        likeable_type: likeable.model_type,
      };
    });
  return apiClient
    .post('users/me/add-to-library', {likeables})
    .then(r => r.data);
}

function getMessage(likeable: Likeable) {
  switch (likeable.model_type) {
    case 'artist':
      return message('Added to your artists');
    case 'album':
      return message('Added to your albums');
    case 'track':
      return message('Added to your liked songs');
    case 'genre':
      return message('Added to your liked genre');
  }
}
